<script setup lang="ts">
import { getProductUrl, getTranslatedProperty } from '@shopware/helpers'
import type { Schemas } from '#shopware'

// Rendered only once FrontendDetailPage has ruled out a seo_redirect, so a product is
// guaranteed to be present here. Everything product-dependent lives in this component
// precisely so that a pending redirect can skip it by not rendering it.
const props = defineProps<{
    productResponse: Schemas['ProductDetailResponse'];
}>();

const { t } = useI18n()
const nuxtApp = useNuxtApp()
const route = useRoute()

const { product } = useProduct(
    props.productResponse.product,
    props.productResponse.configurator ?? [],
)

// Honor Shopware's canonicalProduct reference: if this variant points at a different
// canonical variant, override the canonical link so SEO signals consolidate there.
const canonicalProduct = props.productResponse.product?.canonicalProduct
if (canonicalProduct?.id != null && canonicalProduct.id !== props.productResponse.product?.id) {
    const canonicalPath = getProductUrl(canonicalProduct)
    if (canonicalPath !== '/') {
        const localePath = useLocalePath()
        const { formatLink } = useInternationalization(localePath)
        const { overrideCanonicalUrl } = useCanonicalUrl()
        // Saleschannel basepath (e.g. /fi, /it) is not added by formatLink — prepend it so
        // the override matches the shape of useRequestURL().pathname used elsewhere.
        const baseURL = useRuntimeConfig().app.baseURL
        const basePrefix = baseURL && baseURL !== '/' ? baseURL.replace(/\/$/, '') : ''
        overrideCanonicalUrl(`${basePrefix}${formatLink(canonicalPath)}`)
    }
}

// Check if breadcrumb exists e.g. previous page was a category listing and use them as current breadcrumb
const { breadcrumbs: currentBreadcrumbs, clearBreadcrumbs, pushBreadcrumb } = useBreadcrumbs()

// If no breadcrumb exists currently, generate one from the backend-provided category path
const a4gBreadcrumb = props.productResponse.product?.extensions?.a4g_breadcrumb ?? []
if ((!currentBreadcrumbs.value || currentBreadcrumbs.value.length === 0) && a4gBreadcrumb.length > 0) {
    // The backend delivers the breadcrumb path already ordered root→leaf (root navigation excluded)
    // and with Dynamic-Access-restricted categories removed (A4G-3239). Each entry's `name` is
    // localized and `path` is the canonical SEO path; categories without one fall back to
    // /navigation/{id}, matching how the category listing page links them.
    const categoryBreadcrumb: Breadcrumb[] = a4gBreadcrumb.map((entry) => ({
        name: entry.name ?? '',
        path: entry.path ? '/' + entry.path.replace(/^\/+|\/+$/g, '') : `/navigation/${entry.categoryId}`, // strip any leading/trailing slashes before prepending one
    }))

    categoryBreadcrumb.push({ name: getTranslatedProperty(product.value, 'name') })

    const { clearBreadcrumbs } = useBreadcrumbs(categoryBreadcrumb)
    onBeforeRouteLeave(() => {
        clearBreadcrumbs()
    })
} else {
    pushBreadcrumb({ name: getTranslatedProperty(product.value, 'name') })
    onBeforeRouteLeave(() => {
        clearBreadcrumbs()
    })
}

// Set up browser back interception for product detail pages
const { interceptBrowserBack } = useProductNavigation()
const cleanup = interceptBrowserBack()

const detailPromotionData = ref()
provide('detailPromotionData', detailPromotionData)

onMounted(async () => {
    // Fetch and set promotion data for this product
    const { fetchPromotionData } = useDetailPromotion()
    const detailPromotionResponse = await fetchPromotionData(product.value.id)
    if (detailPromotionResponse != null && detailPromotionResponse.total > 0) {
        detailPromotionData.value = detailPromotionResponse.elements
    }

    nuxtApp.callHook('viewProductDetail', {
        product: product.value,
        breadcrumb: currentBreadcrumbs.value,
    })

    if (route.query.section != null) {
        if (route.query.section === 'reviews') {
            setTimeout(() => {
                nuxtApp.callHook('productReviewsShow')
            }, 1000)
        }
    }
})

onBeforeUnmount(() => {
    if (cleanup) cleanup()
})

useHead({
    title: product?.value?.metaTitle ? product?.value?.metaTitle : product?.value?.name,
    meta: [
        {
            name: 'description',
            content: product?.value?.metaDescription != null && product?.value?.metaDescription.trim() !== '' ?
                product?.value?.metaDescription :
                t('product.detail.seo.description', { name: product?.value?.name })
        }
    ],
})
</script>

<template>
    <LazyProductDetailConfigurablePingProduct v-if="product.manufacturer?.name?.trim().toLowerCase() === 'ping' && product.customFields?.is_configurable" :product-id="product.id" />
    <StructurePage v-if="product.cmsPage" :content="product.cmsPage" />
</template>
